import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';
import { connect } from "react-redux";
import { Dispatch } from 'redux';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ProductItineraryView } from './product-itinerary.view';
import { ContextService } from 'src/core/services/context.service';
import { TourItineraryDataService } from 'src/app/products/tour-itineraries/form/tour-itinerary.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	itineraries: TourItineraryItem[];
}

interface PropsFromDispatch {
	navigateToItinerary: (
		itinerary: TourItineraryItem,
		title: string,
		text: string,
		key: string,
		product?: ProductItem,
		defaultLanguage?: string,
	) => any;
	itinerariesLoaded: (product: ProductItem, itineraries: TourItineraryItem[]) => any;
}

interface Props extends ComponentViewProps<ProductItem> {
	mode: ControlMode;
	contextService: ContextService;
	onModeChanged: (mode: ControlMode) => void;
	defaultLanguage: string;
}

@observer
class ProductItineraryController extends React.Component<Props & PropsFromDispatch & PropsFromState, {}> {
	@resolve
	private toast: ToastNotifications;

	@observable
	private dataService: TourItineraryDataService;

	componentDidMount(): void {
		this.loadData();
	}

	private async loadData(): Promise<void> {
		const { contextService, model, itineraries, itinerariesLoaded } = this.props;
		this.dataService = TourItineraryDataService.make(contextService);

		if (itineraries == null && model.id) {
			this.dataService.addFilter('isDeleted', 'eq', false);
			this.dataService.addFilter('productId', 'eq', model.id);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 25;
			try {
				this.props.setBusy(true);
				const items = await this.dataService.load(query => query);
				itinerariesLoaded(model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				this.props.setBusy(false);
			}
		} else {
			this.dataService.items = itineraries;
		}
	}

	@autobind
	private async handleItemDoubleClick(item: TourItineraryItem): Promise<void> {
		const { model, defaultLanguage, navigateToItinerary, itinerariesLoaded } = this.props;
		const errors = await model.validate(false);
		if (errors.length != 0) {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
			return;
		}

		if (item == null) {
			item = await this.dataService.add(defaultLanguage, model);
			itinerariesLoaded(model, [item]);
		}
		const text = item.dynamicProperties['displayName'][defaultLanguage] || 'New';
		navigateToItinerary(item, 'Itinerary', text, 'itinerary', model, defaultLanguage);
	}

	public render(): React.ReactNode {
		if (this.dataService == null) {
			return null;
		}

		return (
			<ProductItineraryView
				mode={this.props.mode}
				model={this.dataService}
				setBusy={this.props.setBusy}
				onItemDoubleClicked={this.handleItemDoubleClick}
			/>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: ComponentViewProps<ProductItem>
): PropsFromState => ({
	itineraries: (state.otherLoadedEntities.find(e => e.entity == props.model.entity) as ProductItem).itineraries,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	navigateToItinerary: (
		entity,
		title: string,
		text: string,
		key: string,
		product?: ProductItem,
		defaultLanguage?: string,
	) => dispatch(actionCreators.productFormUpNav(entity, title, text, key, product
		? {
			entity: product,
			key: "product",
			title: "Product",
			text: product.dynamicProperties['displayName'][defaultLanguage],
		}
		: undefined)),
	itinerariesLoaded: (
		product: ProductItem,
		itineraries: TourItineraryItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(product, itineraries, "itineraries")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(ProductItineraryController);
export { connected as ProductItineraryController };



// WEBPACK FOOTER //
// ./src/app/products/products/form/parts/product-itinerary.controller.tsx